import { useState, useEffect } from 'react';

/**
 * Global league names cache — fetched once from /api/sports-live/sports
 * Maps Gamma sport slugs to display names (e.g. "fl1" → "Ligue 1", "bun" → "Bundesliga")
 * Replaces hardcoded LEAGUE_CONFIG with live data from Polymarket's /sports endpoint.
 */

let globalLeagueNames: Record<string, string> = {};
let fetchPromise: Promise<void> | null = null;

function fetchLeagueNames(): Promise<void> {
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch('/api/sports-live/sports', { cache: 'no-store' })
    .then(r => r.json())
    .then(res => {
      if (res.success && res.data) {
        const names: Record<string, string> = {};
        for (const group of res.data) {
          for (const league of group.leagues) {
            names[league.slug] = league.name;
          }
        }
        if (Object.keys(names).length > 0) {
          globalLeagueNames = names;
          // Cache in sessionStorage for instant hydration on next page
          try { sessionStorage.setItem('frenflow_league_names', JSON.stringify(names)); } catch {}
        }
      }
    })
    .catch(() => {})
    .finally(() => { fetchPromise = null; });
  return fetchPromise;
}

// Hydrate from sessionStorage on module load (instant, no fetch)
if (typeof window !== 'undefined') {
  try {
    const cached = sessionStorage.getItem('frenflow_league_names');
    if (cached) globalLeagueNames = JSON.parse(cached);
  } catch {}
}

/**
 * Get league display name by slug.
 * Returns the dynamic name from Polymarket's /sports API, or fallback to formatted slug.
 */
export function getLeagueDisplayName(slug: string): string {
  if (!slug) return 'Sports';
  return globalLeagueNames[slug] || slug.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Hook to trigger league names fetch and get the full map.
 * Call this once near the top of your app (e.g. MarketsPageContent).
 */
export function useLeagueNames(): Record<string, string> {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (Object.keys(globalLeagueNames).length > 10) return; // Already loaded
    fetchLeagueNames().then(() => setTick(t => t + 1));
  }, []);

  return globalLeagueNames;
}
